import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ShieldCheck, ArrowRight, Shield, Lock, Activity, Cloud, FileText, CheckCircle, Globe, User, Download, Search, Target, Users, Zap, Mail, ShoppingCart, CheckCircle2, BarChart3, Cpu, MapPin, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SERVICES, WHY_CHOOSE_US, TESTIMONIALS, FAQS, CASE_STUDIES, BLOG_POSTS, PILLARS } from '@/src/constants';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import CaseStudies from '@/src/components/CaseStudies';
import Blog from '@/src/components/Blog';
import ComparisonTable from '@/src/components/ComparisonTable';
import DashboardPreview from '@/src/components/DashboardPreview';
import SEO from '@/src/components/SEO';
import { trackButtonClick } from '@/src/lib/analytics';
import { localBusinessSchema, organizationSchema, websiteSchema } from '@/src/lib/schema';

export default function Home() {
  return (
    <div className="flex flex-col">
      <SEO 
        title="AGRANDINTL – Cybersecurity Consulting for Retail & Cloud"
        description="Professional cybersecurity consulting for retail, e-commerce, and cloud-based businesses. Based in Washington DC, VA, and MD, we provide risk audits and AI compliance solutions."
        keywords="cybersecurity consulting DC, retail security virginia, cloud compliance maryland, pci compliance consulting, cybersecurity risk audit, AGRANDINTL, Fabiyan Anik"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            organizationSchema,
            localBusinessSchema,
            websiteSchema
          ]
        }}
      />
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex flex-col justify-center overflow-hidden pt-32 pb-20">
        {/* Background Elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(59,130,246,0.03),transparent_70%)]" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10 flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.3em] mb-10"
          >
            <Shield className="w-3 h-3" /> Professional Security Consulting
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-8 tracking-tight leading-[1.1] max-w-4xl"
          >
            Cybersecurity Consulting <br />
            <span className="text-accent">for Retail & Cloud Businesses</span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="text-lg md:text-xl text-muted-foreground leading-relaxed font-normal max-w-2xl mb-12 tracking-tight"
          >
            Practical, business-focused security solutions for small to mid-size companies. Serving Washington DC, Virginia, and Maryland with high-impact risk assessments.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 mb-20"
          >
            <Button 
              asChild 
              size="lg" 
              className="h-14 px-10 rounded-xl font-bold text-base transition-all duration-300 shadow-lg shadow-accent/20 hover:shadow-accent/40"
              onClick={() => trackButtonClick('Request Review', 'Hero Section')}
            >
              <Link to="/contact">Request Security Review</Link>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline" 
              className="h-14 px-10 rounded-xl font-bold text-base border-white/10 hover:bg-white/5 transition-all duration-300 backdrop-blur-sm"
              onClick={() => trackButtonClick('View Audit', 'Hero Section')}
            >
              <Link to="/contact?service=audit">Risk Assessment — $299</Link>
            </Button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-[10px] text-muted-foreground/60 uppercase tracking-[0.2em] font-medium"
          >
            All requests are reviewed. If your business is a good fit, we will reach out with next steps.
          </motion.p>

          {/* Trust Indicators - Security Badge Style */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="flex flex-wrap justify-center gap-8 mb-16"
          >
            {[
              { icon: ShieldCheck, label: "Certified Consultants" },
              { icon: MapPin, label: "Serving the DMV Area" },
              { icon: CheckCircle2, label: "Verified Security Audits" }
            ].map((badge, i) => (
              <div key={i} className="flex items-center gap-2.5 px-4 py-2 rounded-xl bg-white/[0.02] border border-white/5 backdrop-blur-sm group hover:border-accent/30 transition-all duration-300">
                <badge.icon className="w-4 h-4 text-accent group-hover:scale-110 transition-transform" />
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground group-hover:text-foreground transition-colors">{badge.label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4"
        >
          <div className="w-[1px] h-24 bg-gradient-to-b from-accent to-transparent animate-pulse" />
        </motion.div>
      </section>

      {/* Awareness Stage: Education & Authority */}
      <section className="py-24 border-y border-white/5 bg-secondary/5 relative overflow-hidden">
        <div className="absolute inset-0 premium-gradient opacity-20" />
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-32">
            <div className="lg:w-1/3 space-y-4 text-center lg:text-left">
              <p className="text-accent text-[9px] font-bold uppercase tracking-[0.4em]">Technical Expertise</p>
              <h2 className="text-3xl md:text-4xl font-display font-bold tracking-tight leading-tight">Securing Modern Business Platforms</h2>
              <p className="text-muted-foreground font-light text-base">We specialize in hardening the platforms that power your business.</p>
            </div>
            <div className="lg:w-2/3 flex flex-wrap items-center justify-center lg:justify-end gap-12 md:gap-20 opacity-40 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-1000">
              {[
                { name: "AWS", icon: Cloud },
                { name: "Azure", icon: Shield },
                { name: "Google Cloud", icon: Globe },
                { name: "Kubernetes", icon: Activity },
                { name: "Terraform", icon: Lock }
              ].map((tech, i) => (
                <div key={tech.name} className="flex flex-col items-center gap-3 group">
                  <tech.icon className="w-10 h-10 group-hover:text-accent transition-colors duration-500" />
                  <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground group-hover:text-foreground transition-colors">{tech.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Offer: Cybersecurity Risk Audit */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 md:p-16 border-white/5 bg-white/[0.01] relative overflow-hidden group"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
              <div className="space-y-10">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-widest">
                    Essential Security
                  </div>
                  <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight leading-tight">Cybersecurity <br /><span className="text-accent">Risk Audit</span></h2>
                  <p className="text-base md:text-lg text-muted-foreground font-normal leading-relaxed max-w-xl">
                    Stop guessing about your security. Our professional audit identifies critical gaps in your systems and provides a clear, prioritized plan to fix them.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {[
                    { label: "Vulnerability Review", desc: "Find exploitable weaknesses", icon: Search },
                    { label: "Risk Score", desc: "Understand your threat level", icon: Activity },
                    { label: "Key Findings", desc: "Professional security summary", icon: FileText },
                    { label: "Action Plan", desc: "Step-by-step fix guide", icon: Target }
                  ].map((item, i) => (
                    <div key={i} className="space-y-2 group/item">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent group-hover/item:bg-accent group-hover/item:text-background transition-all duration-300">
                          <item.icon className="w-5 h-5" />
                        </div>
                        <span className="text-lg font-bold font-display tracking-tight text-foreground group-hover/item:text-accent transition-colors">{item.label}</span>
                      </div>
                      <p className="text-muted-foreground text-sm font-normal pl-13">{item.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="pt-10 border-t border-white/5 flex flex-col sm:flex-row items-center gap-10">
                  <div className="text-center sm:text-left">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-[0.3em] font-bold mb-2">Starting At Only</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-display font-bold text-accent tracking-tighter">$299</span>
                      <span className="text-muted-foreground text-xs font-medium">/ audit</span>
                    </div>
                  </div>
                  <Button asChild size="lg" className="h-16 px-12 rounded-xl font-bold text-lg transition-all duration-300">
                    <Link to="/contact?service=audit">Submit for Risk Assessment</Link>
                  </Button>
                </div>
              </div>

              <div className="relative">
                <div className="relative aspect-square max-w-md mx-auto bg-white/[0.02] rounded-3xl border border-white/10 p-12 flex flex-col items-center justify-center text-center overflow-hidden">
                  <div className="relative z-10 space-y-8 w-full">
                    <div className="w-24 h-24 rounded-2xl bg-accent/10 flex items-center justify-center text-accent mb-4 mx-auto">
                      <Shield className="w-12 h-12" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-6xl md:text-7xl font-display font-bold text-foreground tracking-tighter">84<span className="text-accent">/100</span></p>
                      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">Average Risk Score Identified</p>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        whileInView={{ width: '84%' }}
                        transition={{ duration: 1.5, ease: "easeOut" }}
                        className="h-full bg-accent" 
                      />
                    </div>
                    <p className="text-sm text-muted-foreground font-normal italic">
                      "The audit identified critical gaps that we were able to fix immediately, significantly reducing our risk profile."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Business Pillars */}
      <section className="py-24 bg-secondary/5 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="text-center mb-20 space-y-4">
            <p className="text-accent text-[9px] font-bold uppercase tracking-[0.4em]">Our Ecosystem</p>
            <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight leading-tight">Core Business <br /><span className="text-accent">Pillars</span></h2>
            <p className="text-base md:text-lg text-muted-foreground font-normal max-w-2xl mx-auto leading-relaxed">
              A multi-industry framework designed to secure, automate, and scale modern enterprise operations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((pillar, i) => (
              <motion.div
                key={pillar.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
              >
                <Link to={pillar.href} className="block group h-full">
                  <Card className="glass-card h-full p-8 rounded-2xl border-white/5 hover:border-accent/20 transition-all duration-300 overflow-hidden relative">
                    <CardContent className="p-0 space-y-6 relative z-10">
                      <div className="w-12 h-12 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-all duration-300">
                        <pillar.icon className="w-6 h-6" />
                      </div>
                      <div className="space-y-3">
                        <h3 className="text-xl font-display font-bold tracking-tight group-hover:text-accent transition-colors duration-300">{pillar.title}</h3>
                        <p className="text-sm text-muted-foreground font-normal leading-relaxed">{pillar.description}</p>
                      </div>
                      <div className="flex items-center gap-2 text-accent font-bold text-[10px] uppercase tracking-widest pt-2">
                        Explore Pillar <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-2" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Presence Section */}
      <section className="py-24 relative overflow-hidden bg-secondary/5">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="space-y-4">
                <p className="text-accent text-[9px] font-bold uppercase tracking-[0.4em]">Meet the Founder</p>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-4">
                    <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight leading-tight">Fabiyan Anik</h2>
                    <a 
                      href="https://www.linkedin.com/in/fabiyan-anik" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-2 flex items-center gap-2 text-muted-foreground hover:text-accent transition-all group/lnk"
                      title="View professional profile"
                    >
                      <Linkedin className="w-5 h-5 md:w-6 md:h-6 transition-transform group-hover/lnk:scale-110" />
                      <span className="text-[10px] uppercase tracking-widest font-bold opacity-0 group-hover/lnk:opacity-100 transition-opacity whitespace-nowrap hidden sm:block">View LinkedIn Profile</span>
                    </a>
                  </div>
                  <p className="text-[10px] font-bold text-accent/60 uppercase tracking-[0.2em]">Verified Cybersecurity Professional • Active Network</p>
                </div>
                <p className="text-base text-muted-foreground leading-relaxed">
                  With a dedicated background in cybersecurity, Fabiyan Anik founded AGRANDINTL to provide businesses with a practical, business-focused approach to security. He understands that for small and mid-size companies, security must be an enabler, not a bottleneck.
                </p>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Based in the DMV area, Fabiyan and his team serve businesses across Washington DC, Virginia, and Maryland, helping them navigate complex threats with clear, actionable intelligence.
                </p>
              </div>
              <div className="flex flex-col gap-4 items-center">
                <Button asChild className="h-12 px-8 rounded-xl font-bold text-xs transition-all duration-300">
                  <Link to="/contact">Request Security Review</Link>
                </Button>
                <p className="text-[10px] text-muted-foreground/40 uppercase tracking-widest font-medium">Professional Consultation Request • High-Intent Review</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/5] max-w-md mx-auto rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] flex flex-col relative group">
                <div className="absolute inset-0 bg-accent/5 group-hover:bg-accent/10 transition-colors duration-500 flex items-center justify-center">
                  <User className="w-32 h-32 text-accent/10 group-hover:scale-110 transition-transform duration-700" />
                </div>
                
                <div className="mt-auto p-8 relative z-10 space-y-4">
                  <div className="glass-card p-6 rounded-xl border-white/10 backdrop-blur-xl">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent mb-2">Founder's Pledge</p>
                    <p className="text-sm italic text-foreground/90 leading-relaxed font-serif">
                      "My goal is simple: to make high-end security accessible to the businesses that power our local economy. No jargon, just results."
                    </p>
                    <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                      <div>
                        <p className="text-lg font-display font-bold">Fabiyan Anik</p>
                        <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest">Founder @ AGRANDINTL</p>
                      </div>
                      <a 
                        href="https://www.linkedin.com/in/fabiyan-anik" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center hover:border-accent hover:bg-accent/5 transition-all group/linkedin"
                        title="View professional profile"
                      >
                        <Linkedin className="w-4 h-4 text-muted-foreground group-hover/linkedin:text-accent transition-colors" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Help Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="text-center mb-16 space-y-3">
            <p className="text-accent text-[9px] font-bold uppercase tracking-[0.4em]">Who We Help</p>
            <h2 className="text-2xl md:text-4xl font-display font-bold tracking-tight leading-tight">Tailored Security for <span className="text-accent">Your Industry</span></h2>
            <p className="text-sm text-muted-foreground font-normal max-w-xl mx-auto leading-relaxed">
              If you rely on technology to run your business, we help you secure it.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                title: "Retail Businesses", 
                desc: "Securing payment systems and protecting customer data for businesses handling payments.", 
                icon: ShoppingCart 
              },
              { 
                title: "E-commerce Stores", 
                desc: "Protecting Shopify and custom online platforms from account takeovers and payment fraud.", 
                icon: Globe 
              },
              { 
                title: "Small to Mid-Size Companies", 
                desc: "Growing organizations scaling their digital footprint and managing risk securely.", 
                icon: Users 
              },
              { 
                title: "Cloud-Based Businesses", 
                desc: "SaaS platforms and cloud-native infrastructure requiring hardened protection.", 
                icon: Cloud 
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card p-6 rounded-2xl border-white/5 bg-white/[0.01] hover:border-accent/20 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center text-accent mb-4">
                  <item.icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-display font-bold mb-2">{item.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Example Engagement Section */}
      <section className="py-24 bg-accent/[0.02] border-y border-white/5 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-accent text-[9px] font-bold uppercase tracking-[0.4em]">Case Study</p>
                <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight">Example <span className="text-accent">Engagement</span></h2>
                <p className="text-muted-foreground text-base leading-relaxed max-w-xl">
                  See how we helped a local DC retail group identify critical vulnerabilities and harden their infrastructure for long-term resilience.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex flex-col gap-4">
                  {[
                    { label: "Client", value: "Retail Business (DC Area)" },
                    { label: "Challenge", value: "Potential exposure in payment systems" }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 items-center">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground w-20">{item.label}</span>
                      <span className="text-base font-medium text-foreground">{item.value}</span>
                    </div>
                  ))}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-6">
                  {[
                    { title: "Assessment", desc: "Identified multiple security gaps and outdated configurations.", icon: Search },
                    { title: "Result", desc: "Provided a clear action plan and helped reduce risk exposure significantly.", icon: ShieldCheck },
                    { title: "Outcome", desc: "Business now operates with improved security and confidence.", icon: CheckCircle2 }
                  ].map((item, i) => (
                    <div key={i} className="glass-card p-5 rounded-xl border-white/5 bg-white/[0.01] last:sm:col-span-2">
                       <div className="flex items-center gap-3 mb-2">
                        <item.icon className="w-4 h-4 text-accent" />
                        <h4 className="font-bold text-sm tracking-tight">{item.title}</h4>
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="glass-card aspect-[4/3] rounded-3xl border-white/10 bg-white/[0.01] overflow-hidden flex items-center justify-center p-8 group">
                <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent group-hover:opacity-100 transition-opacity" />
                <div className="relative z-10 text-center space-y-6">
                  <div className="w-20 h-20 rounded-2xl bg-accent/10 flex items-center justify-center text-accent mb-4 mx-auto border border-accent/20">
                    <Activity className="w-10 h-10" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-4xl font-display font-bold tracking-tighter">100%</p>
                    <p className="text-[8px] font-bold uppercase tracking-[0.3em] text-muted-foreground">Successful Remediation</p>
                  </div>
                  <p className="text-xs text-muted-foreground italic max-w-[200px] mx-auto">
                    "Agrandintl's pragmatic approach turned our security from a concern into a competitive advantage."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transparent Pricing Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="text-center mb-16 space-y-3">
            <p className="text-accent text-[9px] font-bold uppercase tracking-[0.4em]">Simple Pricing</p>
            <h2 className="text-2xl md:text-4xl font-display font-bold tracking-tight leading-tight">Investment <span className="text-accent">Models</span></h2>
            <p className="text-sm text-muted-foreground font-normal max-w-xl mx-auto leading-relaxed">
              No hidden fees. Professional security solutions tailored to your business scale.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { 
                title: "Cybersecurity Risk Audit", 
                price: "$299", 
                period: "fixed",
                desc: "Identify critical vulnerabilities and get a prioritized roadmap." 
              },
              { 
                title: "Ongoing Monitoring", 
                price: "$300", 
                period: "/ month",
                desc: "Continuous threat detection and security management starting at $300/mo." 
              },
              { 
                title: "Security Implementation", 
                price: "Custom", 
                period: "pricing",
                desc: "Full-scale security architecture and strategic hardening." 
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card p-8 rounded-2xl border-white/5 bg-white/[0.01] flex flex-col items-center text-center group hover:border-accent/20 transition-all"
              >
                <h3 className="text-lg font-display font-bold mb-4">{item.title}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-display font-bold text-accent tracking-tighter">{item.price}</span>
                  <span className="text-xs text-muted-foreground font-medium ml-1">{item.period}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed mb-8">{item.desc}</p>
                <Button asChild variant="outline" className="w-full h-11 border-accent/20 text-accent hover:bg-accent hover:text-background font-bold text-xs rounded-xl transition-all duration-300">
                  <Link to="/contact">Discuss Plan</Link>
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Magnet Section (Awareness) */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 md:p-16 border-white/5 bg-white/[0.01] relative overflow-hidden group"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
              <div className="space-y-8">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-widest">
                    Intelligence Briefing
                  </div>
                  <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight leading-tight">2026 Cyber <br /><span className="text-accent">Risk Report</span></h2>
                  <p className="text-base md:text-lg text-muted-foreground font-normal leading-relaxed max-w-xl">
                    Get the definitive guide to the evolving threat landscape. Learn how elite organizations are defending against AI-driven attacks.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <p className="text-[9px] font-bold uppercase tracking-[0.3em] text-muted-foreground">Inside the Briefing:</p>
                  <ul className="space-y-3">
                    {[
                      "AI-Driven Threat Actor Profiles",
                      "Retail POS Network Vulnerability Map",
                      "Cloud Misconfiguration Benchmarks",
                      "2026 Remediation Budgeting Guide"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-base text-foreground/80 font-normal">
                        <CheckCircle2 className="w-4 h-4 text-accent" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-wrap gap-4 pt-4">
                  <Button asChild className="h-14 px-8 rounded-xl font-bold text-base transition-all duration-300">
                    <Link to="/resources/risk-report">
                      <Download className="w-4 h-4" /> Download Report
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="h-14 px-8 rounded-xl font-bold text-base border-white/10 hover:bg-white/5 transition-all duration-300">
                    <Link to="/contact">
                      Request Briefing
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="hidden lg:block relative">
                <div className="aspect-[3/4] max-w-sm mx-auto bg-white/[0.02] rounded-3xl border border-white/10 p-12 flex flex-col justify-between relative overflow-hidden group">
                  <div className="relative z-10 space-y-8">
                    <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center text-accent">
                      <Shield className="w-7 h-7" />
                    </div>
                    <div className="space-y-3">
                      <div className="w-full h-1.5 bg-accent/30 rounded-full" />
                      <div className="w-3/4 h-1.5 bg-white/10 rounded-full" />
                      <div className="w-full h-1.5 bg-white/10 rounded-full" />
                    </div>
                  </div>
                  <div className="relative z-10 space-y-4">
                    <p className="text-3xl font-display font-bold leading-tight tracking-tight">CYBER RISK <br />REPORT 2026</p>
                    <div className="flex items-center justify-between">
                      <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground font-bold">Intelligence Unit</p>
                      <Lock className="w-3.5 h-3.5 text-accent/40" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <div className="glass-card rounded-3xl p-8 md:p-12 border-white/5 bg-white/[0.01] flex flex-col md:flex-row items-center justify-between gap-10">
            <div className="space-y-4 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-widest">
                Local Presence
              </div>
              <h2 className="text-3xl font-display font-bold tracking-tight">Serving the <span className="text-accent">DMV Area</span></h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                We provide on-site and remote cybersecurity consulting for businesses across Washington DC, Virginia, and Maryland. Our local presence allows us to understand the unique regulatory and business landscape of the region.
              </p>
            </div>
            <div className="flex items-center gap-8">
              <div className="text-center">
                <p className="text-2xl font-display font-bold text-foreground">DC</p>
                <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-bold">District</p>
              </div>
              <div className="w-[1px] h-10 bg-white/10" />
              <div className="text-center">
                <p className="text-2xl font-display font-bold text-foreground">VA</p>
                <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-bold">Virginia</p>
              </div>
              <div className="w-[1px] h-10 bg-white/10" />
              <div className="text-center">
                <p className="text-2xl font-display font-bold text-foreground">MD</p>
                <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-bold">Maryland</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Blog />

      {/* Consideration Stage: Capabilities & Proof */}
      <section className="py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-10">
            <div className="space-y-4 max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.3em]">
                Core Capabilities
              </div>
              <h2 className="text-3xl md:text-5xl font-display font-bold leading-tight tracking-tight">
                Engineered to Protect <br />
                <span className="text-accent">Business Operations</span>
              </h2>
              <p className="text-muted-foreground text-base md:text-lg font-normal leading-relaxed max-w-2xl">
                Cybersecurity is a fundamental business requirement. We harden your environment to ensure your operations remain uninterrupted.
              </p>
            </div>
            <Button asChild variant="link" className="text-accent font-bold group p-0 text-sm hover:no-underline">
              <Link to="/services" className="flex items-center gap-2">
                Explore Full Suite <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-6">
            {/* Large Feature */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="md:col-span-6 lg:col-span-8 group"
            >
              <Card className="glass-card h-full min-h-[300px] relative overflow-hidden group-hover:border-accent/20 transition-all duration-300">
                <CardContent className="p-8 h-full flex flex-col justify-between relative z-10">
                  <div className="space-y-6">
                    <div className="w-10 h-10 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center text-accent">
                      <Shield className="w-5 h-5" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl font-display font-bold">Security Risk Audit</h3>
                      <p className="text-muted-foreground text-base max-w-md font-normal">
                        We identify the gaps that others miss. Our experts perform deep-dive reviews to ensure your business data stays protected.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-6">
                    {["Network Infiltration", "Application Logic", "Social Engineering", "Cloud Escalation"].map(item => (
                      <div key={item} className="flex items-center gap-2 text-xs text-foreground/70">
                        <div className="w-1 h-1 rounded-full bg-accent" /> {item}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Small Feature 1 */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="md:col-span-6 lg:col-span-4"
            >
              <Card className="glass-card h-full bg-accent/5 border-accent/10 hover:bg-accent/10 hover:border-accent/30 transition-all duration-300 group">
                <CardContent className="p-10 flex flex-col justify-between h-full">
                  <div className="space-y-6">
                    <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center text-accent">
                      <Cloud className="w-6 h-6" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-2xl font-display font-bold">Risk Management</h3>
                      <p className="text-muted-foreground text-sm font-normal">
                        Eliminate the financial impact of cyber incidents with proactive risk mitigation and governance.
                      </p>
                    </div>
                  </div>
                  <Button asChild variant="outline" className="mt-8 border-accent/20 hover:bg-accent hover:text-background transition-all duration-300">
                    <Link to="/contact">Get Risk Profile</Link>
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Small Feature 2 */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="md:col-span-3 lg:col-span-4"
            >
              <Card className="glass-card h-full hover:border-accent/30 transition-all duration-300 group">
                <CardContent className="p-10 space-y-6">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                    <Activity className="w-6 h-6" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-2xl font-display font-bold">Compliance Guard</h3>
                    <p className="text-muted-foreground text-sm font-normal">
                      Avoid massive fines. We ensure you meet PCI, SOC2, and HIPAA standards with zero friction.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Medium Feature */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="md:col-span-3 lg:col-span-8"
            >
              <Card className="glass-card h-full relative overflow-hidden hover:border-accent/30 transition-all duration-300 group">
                <CardContent className="p-10 relative z-10 flex flex-col md:flex-row gap-8 items-center">
                  <div className="space-y-2 flex-1">
                    <h3 className="text-2xl font-display font-bold">Ransomware Defense</h3>
                    <p className="text-muted-foreground text-sm font-normal">
                      Protect your operations from being held hostage. We implement multi-layered recovery and prevention systems.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    {["PCI DSS", "SOC2"].map((tag, i) => (
                      <div 
                        key={tag}
                        className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[9px] font-bold tracking-widest uppercase"
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Consideration Stage: Proof & Methodology */}
      <CaseStudies />

      {/* Testimonials Section */}
      <section className="py-40 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-24 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.4em]">
              Client Testimonials
            </div>
            <h2 className="text-4xl md:text-6xl font-display font-bold tracking-tight leading-tight">Strategic <span className="text-accent">Impact</span></h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-normal leading-relaxed">
              Hear from the business owners and managers who trust Agrandintl with their security and data.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
              >
                <Card className="glass-card p-10 h-full relative overflow-hidden border-white/10 rounded-3xl group">
                  <div className="absolute top-0 left-0 w-1 h-full bg-accent opacity-20 group-hover:opacity-100 transition-opacity duration-500" />
                  <CardContent className="p-0 space-y-8">
                    <div className="flex gap-1.5">
                      {[1, 2, 3, 4, 5].map(star => (
                        <div key={star} className="w-4 h-4 bg-accent/10 rounded-full flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-accent rounded-full" />
                        </div>
                      ))}
                    </div>
                    <p className="text-xl md:text-2xl font-serif italic font-normal leading-relaxed text-foreground/90">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center gap-6 pt-6 border-t border-white/5">
                      <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center text-accent font-bold text-xl">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-bold text-foreground text-lg tracking-tight">{testimonial.author}</p>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-[0.3em] font-bold mt-1">{testimonial.role} | {testimonial.company}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Why Choose Us Section - Bento Grid Layout */}
      <section className="py-40 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mb-24 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em]">
              The AGRAND Advantage
            </div>
            <h2 className="text-4xl md:text-6xl font-display font-bold leading-tight tracking-tight">
              Why Businesses <br />
              <span className="text-accent">Trust AGRANDINTL</span>
            </h2>
            <p className="text-muted-foreground text-lg md:text-xl font-normal leading-relaxed max-w-2xl">
              We don't just provide security tools; we provide strategic partnership. Our focus is on building resilient organizations that can thrive in an increasingly hostile digital landscape.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            {/* Mission Card - Large Bento Item */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="md:col-span-4 glass-card rounded-3xl p-10 md:p-12 relative overflow-hidden group border-white/5"
            >
              <div className="flex items-center gap-6 relative z-10 mb-10">
                <div className="w-16 h-16 rounded-xl bg-accent flex items-center justify-center text-background shadow-lg">
                  <ShieldCheck className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold font-display tracking-tight">Our Mission</h3>
                  <p className="text-accent text-[9px] font-bold uppercase tracking-[0.2em]">Protecting the Future</p>
                </div>
              </div>
              <p className="text-2xl md:text-3xl font-serif italic text-foreground/90 leading-snug relative z-10 mb-10">
                "Our goal is to democratize enterprise-grade security, making it accessible and effective for businesses of all sizes."
              </p>
              <div className="pt-10 border-t border-white/10 relative z-10 flex flex-wrap items-center justify-between gap-6">
                <div>
                  <p className="font-bold font-display text-lg tracking-tight">Fabiyan Anik</p>
                  <p className="text-muted-foreground text-[10px] uppercase tracking-widest font-bold">Founder & Lead Consultant | Cybersecurity Expert</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {["ISO 27001", "SOC2 Type II", "GDPR"].map(cert => (
                    <div key={cert} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[9px] font-bold uppercase tracking-widest text-accent">
                      {cert}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Feature 1 - Retail-First */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="md:col-span-2 glass-card rounded-3xl p-10 flex flex-col justify-between group border-white/5 hover:border-accent/30 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-all duration-300 mb-6">
                <Users className="w-6 h-6" />
              </div>
              <div className="space-y-3">
                <h4 className="text-xl font-bold font-display tracking-tight">{WHY_CHOOSE_US[0].title}</h4>
                <p className="text-muted-foreground leading-relaxed font-normal text-sm">{WHY_CHOOSE_US[0].description}</p>
              </div>
            </motion.div>

            {/* Feature 2 - Cloud-Native */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="md:col-span-2 glass-card rounded-3xl p-10 flex flex-col justify-between group border-white/5 hover:border-accent/30 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-all duration-300 mb-6">
                <Zap className="w-6 h-6" />
              </div>
              <div className="space-y-3">
                <h4 className="text-xl font-bold font-display tracking-tight">{WHY_CHOOSE_US[1].title}</h4>
                <p className="text-muted-foreground leading-relaxed font-normal text-sm">{WHY_CHOOSE_US[1].description}</p>
              </div>
            </motion.div>

            {/* Feature 3 - Intelligence-Led */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="md:col-span-2 glass-card rounded-3xl p-10 flex flex-col justify-between group border-white/5 hover:border-accent/30 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-all duration-300 mb-6">
                <Target className="w-6 h-6" />
              </div>
              <div className="space-y-3">
                <h4 className="text-xl font-bold font-display tracking-tight">{WHY_CHOOSE_US[2].title}</h4>
                <p className="text-muted-foreground leading-relaxed font-normal text-sm">{WHY_CHOOSE_US[2].description}</p>
              </div>
            </motion.div>

            {/* Feature 4 - Zero-Breach */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="md:col-span-2 glass-card rounded-3xl p-10 flex flex-col justify-between group border-white/5 hover:border-accent/30 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-all duration-300 mb-6">
                <CheckCircle className="w-6 h-6" />
              </div>
              <div className="space-y-3">
                <h4 className="text-xl font-bold font-display tracking-tight">{WHY_CHOOSE_US[3].title}</h4>
                <p className="text-muted-foreground leading-relaxed font-normal text-sm">{WHY_CHOOSE_US[3].description}</p>
              </div>
            </motion.div>
          </div>

          <div className="mt-20 flex items-center justify-center gap-4">
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="w-10 h-10 rounded-full border-2 border-background bg-secondary flex items-center justify-center text-[9px] font-bold">
                  {i === 4 ? "50+" : <User className="w-4 h-4 text-accent/40" />}
                </div>
              ))}
            </div>
            <p className="text-base text-muted-foreground font-medium tracking-tight">Trusted by 50+ Business Owners & IT Professionals</p>
          </div>
        </div>
      </section>

      {/* How It Works Section - Now matching 'What Happens Next' steps */}
      <section className="py-40 relative overflow-hidden bg-secondary/5">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="text-center mb-24 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.4em]">
              Our Process
            </div>
            <h2 className="text-4xl md:text-6xl font-display font-bold tracking-tight leading-tight">
              What Happens <span className="text-accent">Next</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground font-normal max-w-2xl mx-auto leading-relaxed">
              A clear, 4-step path to securing your business. No pressure, just practical guidance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {[
              { 
                step: "01", 
                title: "Review", 
                desc: "We review your request and business details to understand your specific environment.",
                icon: Search
              },
              { 
                step: "02", 
                title: "Consultation", 
                desc: "We schedule a short consultation to discuss your concerns and security goals.",
                icon: Users
              },
              { 
                step: "03", 
                title: "Assessment", 
                desc: "We assess your current security posture, identifying actual gaps in your defense.",
                icon: ShieldCheck
              },
              { 
                step: "04", 
                title: "Strategy", 
                desc: "You receive clear recommendations and a prioritized strategy for your next steps.",
                icon: Target
              }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="glass-card rounded-3xl p-8 space-y-6 relative z-10 group hover:border-accent/30 transition-all duration-300"
              >
                <div className="flex justify-between items-start">
                  <div className="w-12 h-12 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-all duration-300">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <span className="text-2xl font-display font-black text-accent/10 group-hover:text-accent/20 transition-colors">{item.step}</span>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-display font-bold tracking-tight group-hover:text-accent transition-colors">{item.title}</h3>
                  <p className="text-sm text-muted-foreground font-normal leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <DashboardPreview />

      {/* Complytics Product Section */}
      <section className="py-40 relative overflow-hidden">
        <div className="absolute inset-0 bg-accent/[0.02]" />
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-32 items-center">
            <div className="space-y-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em]">
                Flagship Technology
              </div>
              <h2 className="text-4xl md:text-6xl font-display font-bold leading-tight tracking-tight">
                Complytics <br />
                <span className="text-accent">AI Compliance</span>
              </h2>
              <p className="text-muted-foreground text-lg md:text-xl font-normal leading-relaxed max-w-2xl">
                Automate compliance visibility, monitor risk posture in real-time, and simplify security readiness with our proprietary AI-powered platform.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  "Real-time risk monitoring",
                  "Automated compliance mapping",
                  "AI-driven gap analysis",
                  "Executive visibility dashboards"
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-3 text-base font-normal group">
                    <div className="w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-all duration-300">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </div>
                    {feature}
                  </div>
                ))}
              </div>
              <div className="pt-6 flex flex-col sm:flex-row items-center gap-6">
                <Button asChild size="lg" className="h-16 px-10 rounded-xl font-bold text-lg transition-all duration-300">
                  <Link to="/complytics">Explore Complytics Platform</Link>
                </Button>
              </div>
              <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/[0.02] border border-white/5 w-fit">
                <Cpu className="w-4 h-4 text-accent" />
                <p className="text-[9px] text-muted-foreground font-bold uppercase tracking-[0.2em]">
                  A product by AGRANDINTL
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="relative glass-card rounded-3xl p-4 border-white/5 overflow-hidden">
                <div className="bg-background/80 rounded-2xl p-8 aspect-video flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 cyber-grid opacity-5" />
                  <div className="relative z-10 w-full h-full flex flex-col gap-6">
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-500/30" />
                        <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/30" />
                        <div className="w-2.5 h-2.5 rounded-full bg-green-500/30" />
                      </div>
                      <div className="px-2.5 py-1 rounded-full bg-accent/10 border border-accent/20 text-[8px] font-bold text-accent uppercase tracking-widest">
                        AI Engine Active
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 h-full">
                      <div className="col-span-2 bg-white/5 rounded-xl border border-white/5 p-6 space-y-4">
                        <div className="w-1/2 h-3 bg-white/10 rounded-full" />
                        <div className="grid grid-cols-2 gap-4">
                          <div className="h-20 bg-accent/5 rounded-lg border border-accent/10" />
                          <div className="h-20 bg-white/5 rounded-lg border border-white/5" />
                        </div>
                        <div className="space-y-2">
                          <div className="w-full h-1.5 bg-white/5 rounded-full" />
                          <div className="w-3/4 h-1.5 bg-white/5 rounded-full" />
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-xl border border-white/5 p-6 space-y-6">
                        <div className="w-full h-3 bg-white/10 rounded-full" />
                        <div className="flex flex-col gap-3">
                          {[1, 2, 3, 4].map(i => (
                            <div key={i} className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-accent/50" />
                              <div className="flex-1 h-1.5 bg-white/5 rounded-full" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-40 relative overflow-hidden border-t border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="text-center mb-20 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.3em]">
              Transparency
            </div>
            <h2 className="text-4xl md:text-6xl font-display font-bold tracking-tight">Common <span className="text-accent">Questions</span></h2>
          </div>
          <Accordion className="w-full space-y-4">
            {FAQS.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/5 bg-white/[0.02] rounded-2xl px-8 transition-all hover:bg-white/[0.04]">
                <AccordionTrigger className="text-left font-display font-medium text-lg py-6 hover:text-accent transition-colors hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6 font-normal">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Decision Stage: Pricing & Commitment */}
      <ComparisonTable />

      {/* Security Guarantees Section (Decision Stage) */}
      <section className="py-40 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <div className="glass-card rounded-3xl p-12 md:p-20 border-accent/10 bg-accent/[0.02] relative overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 items-center">
              <div className="lg:col-span-1 space-y-8">
                <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center text-accent">
                  <ShieldCheck className="w-8 h-8" />
                </div>
                <h2 className="text-3xl font-display font-bold tracking-tight">The AGRANDINTL <br /> <span className="text-accent">Assurance</span></h2>
                <p className="text-muted-foreground font-normal leading-relaxed">
                  We stand behind our intelligence. Our commitment to your security is backed by rigorous standards and professional guarantees.
                </p>
              </div>
              <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  { 
                    title: "Incident Response Pledge", 
                    desc: "If a security event occurs in a system we've actively hardened, we provide prioritized incident support to help your team recover and stay resilient." 
                  },
                  { 
                    title: "Compliance Shield", 
                    desc: "We guarantee that our recommended remediations will meet or exceed the requirements for PCI DSS v4.0 and cloud-native compliance frameworks." 
                  },
                  { 
                    title: "Confidentiality First", 
                    desc: "All data and intelligence gathered during our engagements are protected by military-grade encryption and strict NDAs." 
                  },
                  { 
                    title: "Expertise That Scales", 
                    desc: "Every engagement is led by professionals with real-world experience in protecting cloud infrastructure and retail operations." 
                  }
                ].map((guarantee, i) => (
                  <div key={i} className="p-8 rounded-2xl bg-white/[0.02] border border-white/5 space-y-4 hover:bg-white/[0.04] transition-colors">
                    <h3 className="text-lg font-bold font-display tracking-tight text-accent">{guarantee.title}</h3>
                    <p className="text-sm text-muted-foreground font-normal leading-relaxed">{guarantee.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Summary: Who We Help Section */}
      <section className="py-20 bg-secondary/5">
        <div className="container mx-auto px-4 md:px-8">
          <div className="glass-card rounded-3xl p-12 border-white/5 bg-background/50 backdrop-blur-sm">
            <h3 className="text-2xl font-display font-bold tracking-tight mb-8 text-center">Helping Businesses Secure Their Technology</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { label: "Retail Businesses", desc: "Handling payments & POS networks" },
                { label: "E-commerce Stores", desc: "Securing online revenue streams" },
                { label: "SMBs", desc: "Small to mid-size companies scaling securely" },
                { label: "Cloud Businesses", desc: "Protecting AWS, Azure & SaaS apps" }
              ].map((item, i) => (
                <div key={i} className="space-y-2 text-center md:text-left">
                  <p className="font-bold text-accent">{item.label}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-40 relative overflow-hidden">
        <div className="absolute inset-0 bg-accent/[0.02] z-0" />
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="glass-card rounded-3xl p-16 md:p-32 text-center space-y-12 max-w-5xl mx-auto overflow-hidden relative border-accent/10">
            <div className="absolute inset-0 cyber-grid opacity-5" />
            <div className="relative z-10 space-y-10">
              <h2 className="text-5xl md:text-8xl font-display font-bold tracking-tight leading-tight">
                Secure Your <br />
                <span className="text-accent">Business Today</span>
              </h2>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto font-normal leading-relaxed">
                Don't wait for a breach to happen. Partner with elite intelligence to build your organization's resilience.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-8">
                <Button asChild size="lg" className="w-full sm:w-auto font-bold text-lg h-16 px-12 rounded-xl transition-all duration-500">
                  <Link to="/contact">Schedule a Consultation</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="w-full sm:w-auto font-bold text-lg h-16 px-12 rounded-xl border-white/10 hover:bg-white/5 transition-all duration-500">
                  <Link to="/services">View All Services</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
